import { supabase } from './supabase';
import { Tables } from '../types/database.types';

export type Season = Tables<'seasons'>;
export type Reality = Tables<'realities'>;

export interface SeasonWithReality extends Season {
  reality?: Reality;
  contestants_count?: number;
  polls_count?: number;
}

export const seasonsService = {
  // Obtener todas las temporadas con información del reality
  async getAll(): Promise<SeasonWithReality[]> {
    const { data, error } = await supabase
      .from('seasons')
      .select(`
        *,
        reality:realities(*)
      `)
      .order('year', { ascending: false })
      .order('number', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Obtener temporadas por reality
  async getByReality(realityId: string): Promise<SeasonWithReality[]> {
    const { data, error } = await supabase
      .from('seasons')
      .select(`
        *,
        reality:realities(*)
      `)
      .eq('reality_id', realityId)
      .order('number', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Obtener una temporada por ID
  async getById(id: string): Promise<SeasonWithReality | null> {
    const { data, error } = await supabase
      .from('seasons')
      .select(`
        *,
        reality:realities(*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Crear una nueva temporada
  async create(season: Omit<Season, 'id' | 'created_at' | 'updated_at'>): Promise<Season> {
    // Si se marca como activa, desactivar otras temporadas del mismo reality
    if (season.active) {
      await this.deactivateOthers(season.reality_id);
    }

    const { data, error } = await supabase
      .from('seasons')
      .insert(season)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Actualizar una temporada
  async update(id: string, season: Partial<Omit<Season, 'id' | 'created_at' | 'updated_at'>>): Promise<Season> {
    // Si se marca como activa, desactivar otras temporadas del mismo reality
    if (season.active && season.reality_id) {
      await this.deactivateOthers(season.reality_id, id);
    }

    const { data, error } = await supabase
      .from('seasons')
      .update({ ...season, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Activar/Desactivar una temporada
  async toggleActive(id: string, active: boolean): Promise<Season> {
    // Obtener la temporada para saber su reality_id
    const season = await this.getById(id);
    if (!season) throw new Error('Temporada no encontrada');

    // Si se activa, desactivar otras del mismo reality
    if (active) {
      await this.deactivateOthers(season.reality_id, id);
    }

    const { data, error } = await supabase
      .from('seasons')
      .update({ active, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Desactivar otras temporadas del mismo reality (excepto la actual)
  async deactivateOthers(realityId: string, exceptId?: string): Promise<void> {
    let query = supabase
      .from('seasons')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('reality_id', realityId)
      .eq('active', true);

    if (exceptId) {
      query = query.neq('id', exceptId);
    }

    const { error } = await query;
    if (error) throw error;
  },

  // Eliminar una temporada (soft delete)
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('seasons')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  // Obtener el siguiente número de temporada para un reality
  async getNextNumber(realityId: string): Promise<number> {
    const { data, error } = await supabase
      .from('seasons')
      .select('number')
      .eq('reality_id', realityId)
      .order('number', { ascending: false })
      .limit(1);

    if (error) throw error;
    return data && data.length > 0 ? data[0].number + 1 : 1;
  },
};
